import { useEffect, useMemo, useState } from "react";
import { fetchContactsPathAware } from "@/lib/contacts/router";
import type { ContactResult } from "@/lib/contacts/types";
import { useDevImpersonationStorage } from "@/hooks/useDevImpersonationStorage";
import { useAuth } from "@/contexts/AuthContext";
import {
  TRAINING_CONTACTS_SEARCH_LIMIT,
  contactCardLabel,
  hasDirectContactInfo,
  type LocationScope,
  type ScopeFilter,
  type SourceFilter,
  type TrainingContactsPanelProps,
  type TrustOverride,
} from "@/components/lead/sections/trainingContactsDirectoryShared";

export function useTrainingContactsDirectory(props: TrainingContactsPanelProps) {
  const auth = useAuth();
  const devImp = useDevImpersonationStorage();
  const { organizationId, locationId, locationName } = props;

  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [contacts, setContacts] = useState<ContactResult[]>([]);
  const [lastSearch, setLastSearch] = useState("");

  const [scopeFilter, setScopeFilter] = useState<ScopeFilter>("all");
  const [sourceFilter, setSourceFilter] = useState<SourceFilter>("all");
  const [directOnly, setDirectOnly] = useState(false);
  const [locationScope, setLocationScope] = useState<LocationScope>(locationId ? "current" : "all");
  const [trustOverride, setTrustOverride] = useState<TrustOverride>("auto");
  const [visibleRows, setVisibleRows] = useState<5 | 10 | 20>(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [trustDebug, setTrustDebug] = useState<{
    effectiveTrustLevel?: number;
    computedTrustLevel?: number;
    requestedTrustOverride?: number | null;
    appliedTrustOverride?: boolean;
    canOverrideTrust?: boolean;
  } | null>(null);

  useEffect(() => {
    if (!devImp.enabled) {
      setTrustOverride("auto");
      return;
    }
    const t = devImp.trustLevelOverride;
    setTrustOverride(t === 1 || t === 2 || t === 3 ? (String(t) as "1" | "2" | "3") : "auto");
  }, [devImp.enabled, devImp.trustLevelOverride]);

  const canUseTrustTestingControls = useMemo(
    () => auth.hasRole("platform_dev", "platform_owner"),
    [auth],
  );

  const runSearch = async (
    q: string,
    selectedLocationScope: LocationScope,
    selectedTrustOverride: TrustOverride = trustOverride,
  ) => {
    if (!organizationId) {
      setContacts([]);
      setError("Pick a workspace organization to load contacts.");
      return;
    }

    setLoading(true);
    setError(null);
    setLastSearch(q.trim());

    const result = await fetchContactsPathAware({
      q: q.trim(),
      limit: TRAINING_CONTACTS_SEARCH_LIMIT,
      organizationId,
      locationId: selectedLocationScope === "current" ? locationId ?? null : null,
      trustLevelOverride: selectedTrustOverride === "auto" ? null : Number(selectedTrustOverride),
    });

    setLoading(false);
    if (!result.ok) {
      setContacts([]);
      setTrustDebug(null);
      setError(result.error);
      return;
    }

    setContacts(result.results || []);
    setTrustDebug(result.debug || null);
  };

  useEffect(() => {
    void runSearch("", locationScope);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationId, locationId]);

  useEffect(() => {
    const preset = String(props.presetQuery || "").trim();
    if (!preset) return;
    setQuery(preset);
    void runSearch(preset, locationScope);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.presetQuery]);

  const filteredContacts = useMemo(() => {
    const filtered = contacts.filter((c) => {
      if (scopeFilter !== "all" && String(c.scope || "").toLowerCase() !== scopeFilter) return false;
      if (sourceFilter !== "all" && String(c.source || "").toLowerCase() !== sourceFilter) return false;
      if (directOnly && !hasDirectContactInfo(c)) return false;
      return true;
    });
    return [...filtered].sort((a, b) => contactCardLabel(a).localeCompare(contactCardLabel(b)));
  }, [contacts, directOnly, scopeFilter, sourceFilter]);

  const totalPages = Math.max(1, Math.ceil(filteredContacts.length / visibleRows));
  const pagedContacts = useMemo(() => {
    const start = (currentPage - 1) * visibleRows;
    return filteredContacts.slice(start, start + visibleRows);
  }, [currentPage, filteredContacts, visibleRows]);

  useEffect(() => {
    setCurrentPage(1);
  }, [query, scopeFilter, sourceFilter, directOnly, locationScope, trustOverride, contacts, visibleRows]);

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [currentPage, totalPages]);

  const recommended = filteredContacts.find((x) => x.recommended) || filteredContacts[0] || null;

  return {
    auth,
    organizationId,
    locationId,
    locationName,
    query,
    setQuery,
    loading,
    error,
    lastSearch,
    scopeFilter,
    setScopeFilter,
    sourceFilter,
    setSourceFilter,
    directOnly,
    setDirectOnly,
    locationScope,
    setLocationScope,
    trustOverride,
    setTrustOverride,
    visibleRows,
    setVisibleRows,
    currentPage,
    setCurrentPage,
    trustDebug,
    canUseTrustTestingControls,
    runSearch,
    contacts,
    filteredContacts,
    totalPages,
    pagedContacts,
    recommended,
  };
}
